import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { DataTable, type Column } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { AppRowActions } from '@/components/apps/AppRowActions';
import { useAppActions } from '@/components/apps/useAppActions';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import { useAdaptivePolledData } from '@/lib/hooks/useAdaptivePolledData';
import type { App } from '@/types/app';

const fetchApps = () => api.apps.listAll();

const FILTERS: { value: string; label: string; statuses: string[] }[] = [
  { value: '', label: 'All statuses', statuses: [] },
  { value: 'running', label: 'Running', statuses: ['running', 'healthy', 'unhealthy', 'starting'] },
  { value: 'stopped', label: 'Stopped', statuses: ['stopped'] },
  { value: 'failed', label: 'Failed', statuses: ['failed'] },
  { value: 'pending', label: 'Pending', statuses: ['pending', 'migrating'] },
];

const appLabel = (a: App) => a.display_name || a.name;

const columns: Column<App>[] = [
  {
    key: 'name',
    header: 'Name',
    sortValue: (a) => appLabel(a).toLowerCase(),
    className: 'max-w-64',
    cell: (a) => (
      <div className="min-w-0">
        <div className="truncate font-medium" title={appLabel(a)}>{appLabel(a)}</div>
        {a.full_subdomain && (
          <div className="truncate text-xs text-muted-foreground" title={a.full_subdomain}>{a.full_subdomain}</div>
        )}
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    sortValue: (a) => a.status,
    cell: (a) => <StatusBadge status={a.status} />,
  },
  {
    key: 'catalog',
    header: 'Catalog / stack',
    className: 'max-w-56',
    cell: (a) => {
      const text = [a.app_catalog?.name, a.stack?.name].filter(Boolean).join(' / ') || '—';
      return <div className="truncate text-muted-foreground" title={text}>{text}</div>;
    },
  },
  {
    key: 'endpoint',
    header: 'Endpoint',
    sortValue: (a) => a.endpoint?.name?.toLowerCase() ?? null,
    cell: (a) => a.endpoint?.name ?? '—',
  },
  {
    key: 'owner',
    header: 'Owner',
    className: 'max-w-40',
    cell: (a) => (
      <div className="truncate font-mono text-xs" title={a.external_user_id}>{a.external_user_id || '—'}</div>
    ),
  },
  {
    key: 'resources',
    header: 'Resources',
    sortValue: (a) => a.stack?.memory_mb ?? null,
    cell: (a) => (
      <span className="tabular-nums whitespace-nowrap">
        {a.stack ? `${a.stack.cpu_limit}c · ${a.stack.memory_mb}M` : '—'}
      </span>
    ),
  },
  {
    key: 'created',
    header: 'Created',
    sortValue: (a) => {
      const t = Date.parse(a.created_at);
      return Number.isNaN(t) ? null : t;
    },
    cell: (a) => {
      const t = Date.parse(a.created_at);
      if (Number.isNaN(t)) return '—';
      return (
        <span className="whitespace-nowrap text-muted-foreground" title={new Date(t).toLocaleString()}>
          {formatAgo(t)}
        </span>
      );
    },
  },
];

const searchText = (a: App) =>
  [a.name, a.display_name, a.external_user_id, a.full_subdomain, a.app_catalog?.name, a.stack?.name, a.endpoint?.name]
    .filter(Boolean)
    .join(' ');

export function AppList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const { data: apps, error, loading, refetch } = useAdaptivePolledData(
    fetchApps,
    (list) =>
      list?.some((a) => a.status === 'pending' || a.status === 'starting' || a.status === 'migrating') ? 5_000 : 30_000,
    30_000,
  );

  const actions = useAppActions({ onChanged: refetch });

  const filter = FILTERS.find((f) => f.value === statusFilter) ?? FILTERS[0];
  const rows = apps && filter.statuses.length > 0 ? apps.filter((a) => filter.statuses.includes(a.status)) : apps;

  const tableColumns: Column<App>[] = [
    ...columns,
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      cell: (a) => <AppRowActions app={a} onAction={actions.request} isBusy={actions.isBusy} />,
    },
  ];

  const empty =
    apps && apps.length > 0
      ? {
          title: 'No apps match this filter',
          action: (
            <Button variant="outline" size="sm" onClick={() => setStatusFilter('')}>
              Clear filter
            </Button>
          ),
        }
      : {
          title: 'No apps yet',
          description: 'Deploy an app from the catalog to see it here.',
          action: (
            <Link to="/apps/new" className={buttonVariants({ size: 'sm' })}>
              Deploy new app
            </Link>
          ),
        };

  const hasData = apps !== undefined;

  return (
    <div className="space-y-3">
      {hasData && error !== null && (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
        >
          <span>Showing last known data — refresh failed.</span>
          <Button variant="ghost" size="xs" onClick={refetch}>
            Retry
          </Button>
        </div>
      )}
      <DataTable<App>
        columns={tableColumns}
        rows={rows}
        getRowKey={(a) => a.id}
        searchText={searchText}
        searchPlaceholder="Search apps…"
        loading={loading}
        error={hasData ? null : error}
        onRetry={refetch}
        empty={empty}
        onRowClick={(a) => navigate(`/apps/${a.id}`)}
        pageSize={20}
        pageResetKey={statusFilter}
        toolbar={
          <>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {rows && (
              <span className="text-sm text-muted-foreground" aria-live="polite">
                {rows.length} {rows.length === 1 ? 'app' : 'apps'}
              </span>
            )}
          </>
        }
      />
      {actions.dialogs}
    </div>
  );
}
